<template>
    <div v-if="domain" class="domains__item" :class="{ 'domains__item-selected': domainOrdered(domain.domain) }">
        <span class="domains__title | u-margin-bottom-responsive">{{ domain.domain }}</span>
        <div class="u-flex-center | u-margin-bottom-responsive">
            <span class="promotion-label | u-display--none-desk u-margin-right" v-if="productPromotionPrice">
                {{ trans('order.promotion-label') }}
            </span>
            <div class="domains__price | u-display--none-desk" v-if="productProlongationPrice !== productPrice">
                <span class="domains__price-label">
                    {{ trans('order.domain-prolongation-price') }}
                </span>
                <br />
                <price-display :price="productProlongationPrice" class="u-margin-right" />
            </div>
            <div class="domains__price | u-display--none-desk">
                <span class="domains__price-label" v-if="shouldDisplayPrice">
                    <template v-if="isFree">
                        {{ trans('order.domain-register-price') }}
                    </template>
                    <template v-else>
                        {{ trans('order.domain-transfer-price') }}
                    </template>
                </span>
                <br />
                <price-display :price="productPrice" />
            </div>
        </div>
        <div class="u-flex-center">
            <span
                class="promotion-label | u-display--none-palm u-display--none-lap u-margin-right"
                v-if="productPromotionPrice"
            >
                {{ trans('order.promotion-label') }}
            </span>
            <div
                class="domains__price | u-display--none-palm u-display--none-lap"
                v-if="productProlongationPrice !== productPrice"
            >
                <span class="domains__price-label">
                    {{ trans('order.domain-prolongation-price') }}
                </span>
                <br />
                <price-display :price="productProlongationPrice" class="u-margin-right" />
            </div>
            <div class="domains__price | u-display--none-palm u-display--none-lap" v-if="shouldDisplayPrice">
                <span class="domains__price-label">
                    <template v-if="isFree">
                        {{ trans('order.domain-register-price') }}
                    </template>
                    <template v-else>
                        {{ trans('order.domain-transfer-price') }}
                    </template>
                </span>
                <br />
                <price-display :price="productPrice" class="u-margin-right" />
            </div>
            <button
                v-if="productCanOrder"
                @click="
                    toggleDomain({
                        domain: domain.domain,
                        name: domain.type,
                        status: displayPriceType,
                        price: productPrice,
                        gross_price: grossProductPrice,
                        period: defaultPeriod
                    })
                "
                type="button"
                dusk="add-domain-button"
                class="btn btn--domain"
                :class="{
                    'btn--ghost': isFree && !domainOrdered(domain.domain),
                    'btn--transfer': isActive && !domainOrdered(domain.domain),
                    'btn--ghost-selected': domainOrdered(domain.domain)
                }"
            >
                <span v-if="domainOrdered(domain.domain)">{{ trans('order.chosen') }}</span>
                <span v-else-if="isFree">{{ trans('order.select') }}</span>
                <span v-else-if="isActive">{{ trans('order.domain-move') }}</span>
            </button>
            <button v-else-if="ownsDomain" class="btn btn--transfer">
                <customer-router-link
                    :to="{ name: 'domainOverview', params: { domain: domain.domain } }"
                    class="link-no-decoration"
                >
                    {{ trans('order.manage') }}
                </customer-router-link>
            </button>
            <small v-else-if="domain.status === 'unknown'">
                {{ trans('order.domain-unknown') }}
            </small>
            <small v-else-if="productIsInvalid">
                {{ trans('order.domain-invalid') }}
            </small>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PriceDisplay from '@/components/Price/PriceDisplay.vue';
import productMixin from '../../mixins/product.js';

export default {
    components: {
        PriceDisplay
    },
    mixins: [productMixin],
    props: {
        ownedDomains: {
            type: Array,
            default: () => []
        },
        domain: {
            type: Object,
            required: true,
            default: () => {}
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            productType: 'extension'
        };
    },
    computed: {
        ...mapGetters({
            domainOrdered: 'order/domain'
        }),
        /**
         * Returns true if a price should be displayed.
         * @return {boolean}
         */
        shouldDisplayPrice() {
            return (this.productCanOrder || this.isActive) && !isNaN(this.productPrice);
        },

        /**
         * Checks if a domain is free.
         *
         * @return {boolean}
         */
        isFree() {
            return this.domain.status === 'free';
        },

        /**
         * Checks if a domain is active.
         *
         * @return {boolean}
         */
        isActive() {
            return this.domain.status === 'active';
        },

        /**
         * Checks if the domain is already yours.
         *
         * @return {boolean}
         */
        ownsDomain() {
            return this.ownedDomains.includes(this.domain.domain);
        },

        /**
         * Returns which price we should display.
         * @return {string}
         */
        displayPriceType() {
            if (this.isFree) {
                return 'registration';
            }

            return 'transfer';
        },
        /**
         * The product plan is the domain product with status information.
         *
         * @return {object}
         */
        productPlan() {
            return {
                domain: this.domain.domain,
                name: this.domain.type,
                status: this.domain.status,
                type: 'extension'
            };
        },

        /**
         * Check if you can order the product.
         *
         * @return {boolean}
         */
        productCanOrder() {
            return this.product && !this.ownsDomain && ['free', 'active'].includes(this.domain.status);
        },

        /**
         * Check if the product cannot be ordered.
         *
         * @return {boolean}
         */
        productIsInvalid() {
            return this.domain.status === 'invalid' || !this.product;
        }
    },
    methods: {
        ...mapActions({
            toggleDomain: 'order/toggleDomain'
        })
    }
};
</script>
